import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { SidekickSummaryDetail } from '../../../../models/sidekick-summary.model';
import { SidekickService } from '../../../../services/sidekick/sidekick.service';
import { ViewService } from '../../../../services/view/view.service';
import { BaseComponent } from '../../../base/base.component';
import {
  ExportButtonComponent,
  ExportFormat,
} from '../../../core/buttons/export-button.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../../core/panels/loading-panel.component';
import { AuSummaryComponent } from './au-summary.component';
import { GlSummaryComponent } from './gl-summary.component';
import { PrSummaryComponent } from './pr-summary.component';
import { WcSummaryComponent } from './wc-summary.component';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-sidekick-summary',
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    LoadingPanelComponent,
    WcSummaryComponent,
    GlSummaryComponent,
    PrSummaryComponent,
    AuSummaryComponent,
    ExportButtonComponent,
  ],
  template: `<app-page-header
      label="Omni AI Summary"
      icon="fa-light fa-sparkles"
      iconSize="24px"
      labelCase="none"
      ><ng-template #right>
        <app-export-button
          [csv]="false"
          [xlsx]="false"
          [pdf]="true"
          (export)="onExport($event)"
          right2
        ></app-export-button></ng-template
    ></app-page-header>
    <app-loading-panel
      [state]="loadingState"
      loadingStyle="Box"
      [loadingHeight]="450"
      [error]="error"
    >
      <ng-template #loaded>
        <div class="tw-flex tw-gap-8 tw-pb-4">
          <div class="tw-w-full">
            @if (isWcClaim) {
              <app-wc-summary [dataSource]="summaryDetail"></app-wc-summary>
            } @else if (isGlClaim) {
              <app-gl-summary [dataSource]="summaryDetail" />
            } @else if (isPrClaim) {
              <app-pr-summary [dataSource]="summaryDetail" />
            } @else if (isAuClaim) {
              <app-au-summary [dataSource]="summaryDetail" />
            }
          </div>
        </div>
        <i>
          This summary has been generated by an artificial intelligence (AI)
          system. We are constantly working to improve the accuracy and quality
          of our AI-Generated content. However, there may still be errors or
          inaccuracies. This does not replace advice from a professional claims
          examiner.
        </i>
      </ng-template>
    </app-loading-panel>`,
  styles: [],
})
export class SidekickSummaryComponent extends BaseComponent implements OnInit {
  @ViewChild(WcSummaryComponent) private wcSummary?: WcSummaryComponent;
  @ViewChild(GlSummaryComponent) private glSummary?: GlSummaryComponent;
  @ViewChild(PrSummaryComponent) private prSummary?: PrSummaryComponent;
  @ViewChild(AuSummaryComponent) private auSummary?: AuSummaryComponent;

  private viewService = inject(ViewService);
  private sidekickService = inject(SidekickService);

  loadingState: LoadingState = 'Loading';
  error: any = null;
  summaryDetail: SidekickSummaryDetail | null = null;

  lob: string;
  claim: any;
  isWcClaim = false;
  isGlClaim = false;
  isPrClaim = false;
  isAuClaim = false;

  @HandleLoadingState({ loadingProperty: 'loadingState', onValue: 'Loading', offValue: 'Loaded' })
  async ngOnInit(): Promise<void> {
    this.view.hideLoading();
    this.claim = this.viewService.currentClaim();
    this.lob = this.claim?.lineOfBusiness;
    this.setClaimLobValues(this.lob);

    if (this.view.authorize('MTTXLaw')) {
      this.router.navigate([
        'view',
        'claim',
        this.claim.recordSource,
        this.claim.recordId,
      ]);
      return;
    }

    if (this.claim) {
      this.summaryDetail = await this.sidekickService.getSummaryDetail(
        this.claim.recordSource,
        this.claim.recordId,
      );

      // Upload to SIR in background (fire-and-forget) when full summary page is accessed
      if (this.summaryDetail) {
        this.sidekickService.uploadSummaryToSIR(
          this.claim.recordSource,
          this.claim.recordId,
        );
      }
    }
  }

  totals(indemnity: number, medical: number, expenses: number) {
    return indemnity + medical + expenses;
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async onExport(format: ExportFormat) {
    if (format === 'pdf' && this.claim && this.summaryDetail) {
      const chartImages = this.captureFinancialChartImages();
      await this.sidekickService.exportPDF(
        this.claim.recordSource,
        this.claim.recordId,
        this.summaryDetail.claimNumber,
        chartImages,
      );
    }
  }

  private captureFinancialChartImages(): string[] {
    if (this.isWcClaim) {
      return this.wcSummary?.captureFinancialChartImages() ?? [];
    }

    if (this.isGlClaim) {
      return this.glSummary?.captureFinancialChartImages() ?? [];
    }

    if (this.isPrClaim) {
      return this.prSummary?.captureFinancialChartImages() ?? [];
    }

    if (this.isAuClaim) {
      return this.auSummary?.captureFinancialChartImages() ?? [];
    }

    return [];
  }

  setClaimLobValues(lob: string) {
    switch (lob) {
      case 'WC':
        this.isWcClaim = true;
        break;
      case 'GL':
        this.isGlClaim = true;
        break;
      case 'PR':
        this.isPrClaim = true;
        break;
      case 'AU':
        this.isAuClaim = true;
        break;
      default:
        this.isWcClaim = true;
        break;
    }
  }
}
